import React, { useState, useEffect, useRef } from "react";
import { Modal, Form, Input, Button, Select, TimePicker, Row, Col, InputNumber, } from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import callApi from "../../constants/API/CallApi";
import { ShowToast, Severty } from "../../helper/toast";
import { addClass, ListBelt, getBelts, ListBranch, ListEmployee, } from "../../constants/API/API";
import useApi from "../../hooks/useApi";

const DAYS_OF_WEEK = [
  { label: "Sunday", value: "SUNDAY" },
  { label: "Monday", value: "MONDAY" },
  { label: "Tuesday", value: "TUESDAY" },
  { label: "Wednesday", value: "WEDNESDAY" },
  { label: "Thursday", value: "THURSDAY" },
  { label: "Friday", value: "FRIDAY" },
  { label: "Saturday", value: "SATURDAY" },
];

const { Option } = Select;

const ClassAdd = ({ visible, setVisible, refresh }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [belts, setBelts] = useState([]);
  const [scheduleFields, setScheduleFields] = useState([{}]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setScheduleFields([{}]);
    }
  }, [visible, form]);

  const getAvailableDays = (currentIndex) => {
    const schedules = form.getFieldValue("schedules") || [];
    const selectedDays = schedules
      .map((schedule, idx) =>
        idx !== currentIndex ? schedule?.dayOfWeek : null
      )
      .filter(Boolean);

    return DAYS_OF_WEEK.filter((day) => !selectedDays.includes(day.value));
  };

  const handleOk = async (values) => {
    setLoading(true);
    try {
      const { schedules, ...rest } = values;

      const isAnyEmpty = schedules.some((schedule, index) => {
        const fieldData = scheduleFields[index];
        const timeRanges = fieldData?.timeRanges || [];

        if (!schedule.dayOfWeek) return true;

        const hasValidSlot = timeRanges.every(
          (t) => t?.value && t?.trainerIds?.length && t?.capacity
        );

        return !hasValidSlot; // means slot/trainer empty
      });

      if (isAnyEmpty) {
        ShowToast(
          "Please fill Day, Slot, and Trainer for all schedules.",
          "error"
        );
        setLoading(false);
        return;
      }

      const formattedSchedules = schedules.map((schedule, index) => {
        const fieldData = scheduleFields[index];

        const timeRanges = fieldData?.timeRanges || [];

        const slots = timeRanges
          .filter((t) => t?.value && t?.trainerIds?.length)
          .flatMap((range) =>
            range.trainerIds.map((trainerId) => {
              return {
                startTime: range.value?.[0]?.format("HH:mm"),
                endTime: range.value?.[1]?.format("HH:mm"),
                trainerId,
                capacity: range?.capacity || 0,
              };
            })
          );

        return {
          dayOfWeek: schedule.dayOfWeek,
          slots,
        };
      });

      const trainerTimeMap = new Set();
      for (const sched of formattedSchedules) {
        for (const slot of sched.slots) {
          const key = `${sched.dayOfWeek}-${slot.startTime}-${slot.endTime}-${slot.trainerId}`;
          if (trainerTimeMap.has(key)) {
            ShowToast(
              `Trainer is already assigned for ${sched.dayOfWeek} at ${slot.startTime}–${slot.endTime}`,
              Severty.ERROR
            );
            setLoading(false);
            return;
          }
          trainerTimeMap.add(key);
        }
      }

      const payload = {
        ...rest,
        schedules: formattedSchedules,
      };

      // console.log(payload);
      callApi({
        callback: addClass,
        payload,
        onSuccess: ({ message }) => {
          ShowToast(message, Severty.SUCCESS);
          form.resetFields();
          setScheduleFields([{}]);
          refresh(new Date().getTime());
          setVisible(false);
          setLoading(false);
        },
        onError: (err) => {
          ShowToast(err, Severty.ERROR);
          setLoading(false);
        },
      });
    } catch (error) {
      console.error("Formatting error", error);
      ShowToast(
        "Something went wrong while formatting schedules",
        Severty.ERROR
      );
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setVisible(false);
    form.resetFields();
    setScheduleFields([{}]);
  };

  const validateClassName = (rule, value) => {
    if (!value) {
      return Promise.reject("Please enter a class name");
    } else if (!/[A-Z][a-zA-Z0-9 ]*$/.test(value)) {
      return Promise.reject(
        "Class name must start with a capital letter and contain only letters, numbers and spaces"
      );
    }
    return Promise.resolve();
  };

  useApi({
    callback: ListBranch,
    dependencies: [refresh],
    shouldCallApi: true,
    onSuccess: ({ list }) => {
      const activeBranches = (list || []).filter((branch) => branch.isActive);
      setBranches(activeBranches);
    },
  });

  const getTrainers = (id) => {
    callApi({
      callback: ListEmployee,
      payload: {
        role: "trainer",
        branchId: id,
      },
      dependencies: [refresh],
      shouldCallApi: true,
      onSuccess: ({ data }) => {
        setTrainers(data?.list || []);
      },
    });
  };

  useApi({
    callback: getBelts,
    payload: selectedBranch ? { branchId: selectedBranch } : null,
    dependencies: [selectedBranch],
    shouldCallApi: Boolean(selectedBranch),
    onSuccess: ({ data }) => {
      setBelts(data || []);
    },
  });


  useEffect(() => {
    const selector = wrapperRef.current?.querySelector(".ant-select-selector");

    if (!selector) return;

    const observer = new ResizeObserver(() => {
      const height = selector.clientHeight;
      if (height > 60) {
        selector.style.setProperty("border-radius", "12px", "important");
      } else {
        selector.style.borderRadius = "24px";
      }
    });

    observer.observe(selector);

    return () => {
      observer.disconnect();
    };
  }, [scheduleFields]);

  return (

    <Modal
      open={visible}
      className="tab_modal"
      title="Add Class"
      okText="Create"
      onCancel={handleCancel}
      okButtonProps={{
        form: "create",
        htmlType: "submit",
        loading: loading,
      }}
      width={1100}
      destroyOnClose
    >
      <Form
        form={form}
        id="create"
        onFinish={handleOk}
        layout="vertical"
        preserve={false}
        initialValues={{
          schedules: [{}],
        }}
      >
        <Row gutter={12}>
          <Col span={24} md={8}>
            <Form.Item
              name="name"
              label="Class Name"
              rules={[{ required: true, validator: validateClassName }]}
            >
              <Input placeholder="Enter class name" maxLength={100} />
            </Form.Item>
          </Col>

          <Col span={24} md={8}>
            <Form.Item
              name="branchId"
              label="Branch"
              rules={[{ required: true, message: "Please select branch!" }]}
            >
              <Select
                onChange={(branchId) => {
                  setSelectedBranch(branchId);
                  getTrainers(branchId);
                }}
                placeholder="Select Branch"
              >
                {branches
                  ?.filter((branch) => branch?.isActive)
                  .map((branch) => (
                    <Select.Option key={branch?._id} value={branch?._id}>
                      {branch?.name}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={24} md={8}>
            <Form.Item
              name="beltId"
              label="Belt"
              rules={[{ required: true, message: "Please select a belt!" }]}
            >
              <Select mode="multiple" placeholder="Select Belt">
                {belts?.map((belt) => (
                  <Select.Option key={belt?._id} value={belt?._id}>
                    {belt?.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={24} md={8}>
            <Form.Item
              name="ageLimitStart"
              label="Start Age Limit"
              rules={[
                { required: true, message: "Please enter valid starting age" },
              ]}
            >
              <Input
                min={3}
                max={100}
                type="number"
                placeholder="Enter Start Age Limit"
              />
            </Form.Item>
          </Col>

          <Col span={24} md={8}>
            <Form.Item
              name="ageLimitEnd"
              label="End Age Limit"
              rules={[
                { required: true, message: "Please enter valid ending age" },
              ]}
            >
              <Input
                min={3}
                max={100}
                type="number"
                placeholder="Enter End Age Limit"
              />
            </Form.Item>
          </Col>


        </Row>

        <Form.List name="schedules">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }, index) => (
                <Row gutter={[6, 6]} className="mb-3" key={key}>
                  {/* Day Selection */}
                  <Col span={24} md={6}>
                    <Form.Item
                      {...restField}
                      name={[name, "dayOfWeek"]}
                      rules={[
                        {
                          required: true,
                          message: "Please select day of week",
                        },
                        {
                          validator: (_, value) => {
                            const schedules =
                              form.getFieldValue("schedules") || [];
                            const duplicateDays = schedules.filter(
                              (s, i) => i !== name && s?.dayOfWeek === value
                            );
                            return duplicateDays.length
                              ? Promise.reject("This day is already selected")
                              : Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select Day"
                        options={getAvailableDays(name)}
                        onChange={(value) => {
                          const newSchedules = [...scheduleFields];
                          newSchedules[index] = {
                            ...newSchedules[index],
                            dayOfWeek: value,
                            timeRanges: newSchedules[index]?.timeRanges || [{}],
                          };
                          setScheduleFields(newSchedules);
                        }}
                        allowClear
                      />
                    </Form.Item>
                  </Col>

                  {/* Time Ranges and Trainers */}
                  <Col span={24} md={17}>
                    <div className="multiple-timeadd">
                      {(scheduleFields?.[index]?.timeRanges || [{}]).map(
                        (range, timeIdx) => (
                          <div key={timeIdx} className="multiple-timeadd-main">
                            <TimePicker.RangePicker
                              format="HH:mm"
                              minuteStep={15}
                              placeholder={["Start Time", "End Time"]}
                              onChange={(value) => {
                                const updated = [...scheduleFields];
                                if (!updated?.[index]) {
                                  updated[index] = { timeRanges: [] };
                                }
                                if (
                                  !Array.isArray(updated?.[index]?.timeRanges)
                                ) {
                                  updated[index].timeRanges = [];
                                }
                                if (!updated?.[index]?.timeRanges[timeIdx]) {
                                  updated[index].timeRanges[timeIdx] = {};
                                }
                                updated[index].timeRanges[timeIdx].value =
                                  value;
                                setScheduleFields(updated);
                              }}
                            />

                            <div ref={wrapperRef} style={{ width: "100%" }}>
                              <Select
                                mode="multiple"
                                placeholder="Select Trainer(s)"
                                style={{
                                  width: "100%",
                                  borderRadius: "24px",
                                  whiteSpace: "normal",
                                }}
                                className="trainer-select"
                                getPopupContainer={(trigger) =>
                                  trigger.parentNode
                                }
                                onChange={(trainerIds) => {
                                  const updated = [...scheduleFields];

                                  if (!updated[index])
                                    updated[index] = { timeRanges: [] };
                                  if (!Array.isArray(updated[index].timeRanges))
                                    updated[index].timeRanges = [];
                                  if (!updated[index].timeRanges[timeIdx])
                                    updated[index].timeRanges[timeIdx] = {};

                                  updated[index].timeRanges[
                                    timeIdx
                                  ].trainerIds = trainerIds;
                                  setScheduleFields(updated);
                                }}
                              >
                                {trainers.map((trainer) => (
                                  <Option key={trainer._id} value={trainer._id}>
                                    {trainer.name}
                                  </Option>
                                ))}
                              </Select>
                            </div>

                            <div style={{ width: "100%" }}>
                              <InputNumber
                                placeholder="Enter Class Capacity"
                                min={0}
                                onChange={(value) => {
                                  const updated = [...scheduleFields];
                                  if (!updated[index])
                                    updated[index] = { timeRanges: [] };
                                  if (!Array.isArray(updated[index].timeRanges))
                                    updated[index].timeRanges = [];
                                  if (!updated[index].timeRanges[timeIdx])
                                    updated[index].timeRanges[timeIdx] = {};

                                  updated[index].timeRanges[timeIdx].capacity =
                                    value;
                                  setScheduleFields(updated);
                                }}
                              />
                            </div>

                            {scheduleFields?.[index]?.timeRanges?.length >
                              1 && (
                                <Button
                                  className="add-minusclass-time"
                                  onClick={() => {
                                    const updated = [...scheduleFields];
                                    updated[index].timeRanges = updated?.[
                                      index
                                    ]?.timeRanges.filter(
                                      (_, idx) => idx !== timeIdx
                                    );
                                    setScheduleFields(updated);
                                  }}
                                >
                                  <MinusCircleOutlined />
                                </Button>
                              )}
                          </div>
                        )
                      )}
                    </div>
                  </Col>
                  <Col span={24} md={1}>
                    <Button
                      onClick={() => {
                        const updated = [...scheduleFields];
                        updated[index].timeRanges = [
                          ...(updated[index]?.timeRanges || []),
                          {},
                        ];
                        setScheduleFields(updated);
                      }}
                      className="add-plusclass-time"
                    >
                      <PlusOutlined />
                    </Button>
                  </Col>
                  {/* Remove Day */}
                  <Col span={24}>
                    {fields.length > 1 && (
                      <Button
                        className="minus-linewrep"
                        onClick={() => {
                          remove(name);
                          const updated = [...scheduleFields];
                          updated?.splice(index, 1);
                          setScheduleFields(updated);
                        }}
                        icon={<MinusCircleOutlined />}
                      >
                        Remove This Day
                      </Button>
                    )}
                  </Col>
                </Row>
              ))}

              {/* Add New Day */}
              <Form.Item>
                <Button
                  className="add-class-schedulebtn"
                  onClick={() => {
                    add();
                    setScheduleFields([...scheduleFields, {}]);
                  }}
                  block
                  icon={<PlusOutlined />}
                  disabled={scheduleFields.length >= 7}
                >
                  Add Class Schedule
                </Button>
                {scheduleFields.length >= 7 && (
                  <div
                    style={{
                      color: "#ff4d4f",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    Maximum 7 days can be scheduled
                  </div>
                )}
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

export default ClassAdd;
